'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useState } from 'react'
import { Formik } from 'formik'

// walrus
import submitForm from '@lib/submit-form'
import { accessibilityLabel } from '@components/kit/_support'
import Box, { BoxProps } from '@components/kit/box'
import Button from '@components/kit/button'
import Form, {
  StatusMessage,
  SubmitButton,
  TextField,
  FormRow,
  FormFooter
} from '@components/kit/form'
import { Logotype } from '@components/kit/logo'
import Section from '@components/kit/section'
import Text from '@components/kit/text'

// lib
import { ENV } from '@lib/env'
import { useAuthStore } from '@store/Auth'
import { FlipperAuthError } from '@store/Auth/types'

/* -----------------------------------------------------------------------------
 * MfaForm
 * -------------------------------------------------------------------------- */

export type MfaFormProps = BoxProps & {
  email: string
  mfaValue?: string
  onBack: () => void
}

const MfaForm = ({ email, mfaValue, onBack, ...props }: MfaFormProps) => {
  const [flipperErrorMsg, setFlipperErrorMsg] = useState('')

  const submit = submitForm((values: { value: string }) =>
    useAuthStore
      .getState()
      .verifyMfa(values.value)
      .then(response => {
        // a verified code stores the session, and Login leaves on its token
        if (!('access_token' in response)) {
          const { errmsg, error } = response as FlipperAuthError
          setFlipperErrorMsg(errmsg || error || 'Invalid verification code')
        }
      })
      .catch(error => {
        setFlipperErrorMsg(error?.message)
      })
  )

  const handleBack = () => {
    useAuthStore.getState().clearMfa()
    onBack()
  }

  const onPressReturn = (e, formik) => {
    // @ts-ignore
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault()
      // unlike the button, Enter still worked while a request was out, and a
      // second login made the first code invalid
      if (!formik?.isSubmitting) formik?.handleSubmit()
    }
  }

  return (
    <Section>
      <Logotype mb='32' />
      <Text variant='h1'>Check your email</Text>
      <Text mt='08'>
        {`Enter the verification code sent to ${email || 'your email'}.`}
      </Text>
      {!!mfaValue && ENV.target !== 'prod' && (
        <Text mt='08' variant='meta'>
          {'Debug code (non-production only): '}
          <Text {...accessibilityLabel('Mfa Debug Code')} variant='meta'>
            {mfaValue}
          </Text>
        </Text>
      )}
      <Box {...props}>
        <Formik
          initialValues={{ value: '' }}
          onSubmit={submit}
          validateOnChange={false}
        >
          {formik => (
            <Form flex='1 0 auto'>
              <FormRow>
                <TextField
                  name='value'
                  label='Verification code'
                  required
                  keyboardType='numeric'
                  maxLength={6}
                  onKeyPress={e => onPressReturn(e, formik)}
                />
              </FormRow>
              <FormFooter flex='1 0 auto' justifyContent='flex-end'>
                <StatusMessage forcedErrorMsg={flipperErrorMsg} />
                <SubmitButton>Verify</SubmitButton>
                <Button variant='tertiary' mt='08' onPress={handleBack}>
                  Back to login
                </Button>
              </FormFooter>
            </Form>
          )}
        </Formik>
      </Box>
    </Section>
  )
}

export default MfaForm
